/**
 * Analyse GPX en flux — EF-GPX-001, EF-GPX-002.
 *
 * `ENF-001` interdit de matérialiser le document : `DOMParser` construirait un
 * arbre de plusieurs centaines de mégaoctets et dépasserait la mémoire
 * disponible avant de dépasser le temps.
 *
 * GPX 1.1 est un format plat et les seuls éléments utiles sont `<trk>`,
 * `<trkseg>`, `<trkpt>`, `<rte>`, `<rtept>`, `<name>` et `<ele>`. Un automate
 * reconnaissant ces balises suffit, et refuse tout ce qu'il ne reconnaît pas
 * comme mal formé.
 *
 * Les points sont accumulés directement dans des `Float64Array` redimensionnés
 * par doublement, sans passer par un tableau d'objets intermédiaire.
 */

import { DomainError } from '../core/errors';

/** Taille maximale d'un fichier GPX, en octets. */
export const MAX_GPX_BYTES = 50 * 1024 * 1024;

/** Nombre maximal de points du parcours sélectionné. */
export const MAX_GPX_POINTS = 500_000;

/** Longueur cumulée minimale du parcours sélectionné, en mètres. */
export const MIN_ROUTE_LENGTH_METERS = 1000;

export type GpxElementType = 'trk' | 'rte';

export interface GpxElement {
  readonly type: GpxElementType;
  readonly name: string | null;
  /** Ordre d'apparition parmi les éléments du même type, à partir de 0. */
  readonly index: number;
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  /** `NaN` lorsque le point ne porte pas d'altitude. */
  readonly ele: Float64Array;
  readonly count: number;
  /** Indices de premier point de chaque segment `<trkseg>`. */
  readonly segmentStarts: readonly number[];
}

export interface GpxDocument {
  /** Traces si le fichier en contient, routes sinon. Jamais mixte. */
  readonly elements: readonly GpxElement[];
  /** Routes entièrement ignorées parce que le fichier contient des traces. */
  readonly ignoredRouteCount: number;
}

/** Accumulateur de points à croissance par doublement. */
class PointBuffer {
  private latData = new Float64Array(1024);
  private lonData = new Float64Array(1024);
  private eleData = new Float64Array(1024);
  private size = 0;
  private readonly segments: number[] = [];

  get count(): number {
    return this.size;
  }

  get segmentStarts(): readonly number[] {
    return this.segments.length === 0 ? [0] : this.segments;
  }

  openSegment(): void {
    this.segments.push(this.size);
  }

  push(lat: number, lon: number, ele: number): void {
    if (this.size === this.latData.length) this.grow();
    this.latData[this.size] = lat;
    this.lonData[this.size] = lon;
    this.eleData[this.size] = ele;
    this.size += 1;
  }

  private grow(): void {
    const capacity = this.latData.length * 2;
    const lat = new Float64Array(capacity);
    const lon = new Float64Array(capacity);
    const ele = new Float64Array(capacity);
    lat.set(this.latData);
    lon.set(this.lonData);
    ele.set(this.eleData);
    this.latData = lat;
    this.lonData = lon;
    this.eleData = ele;
  }

  freeze(): { lat: Float64Array; lon: Float64Array; ele: Float64Array } {
    return {
      lat: this.latData.slice(0, this.size),
      lon: this.lonData.slice(0, this.size),
      ele: this.eleData.slice(0, this.size),
    };
  }
}

interface OpenElement {
  readonly type: GpxElementType;
  readonly index: number;
  name: string | null;
  readonly points: PointBuffer;
}

interface PendingPoint {
  readonly lat: number;
  readonly lon: number;
  ele: number;
}

const malformed = (detail: string): DomainError =>
  new DomainError('gpx/malformed', `Le fichier GPX est mal formé : ${detail}.`, detail);

/**
 * Automate d'analyse. Alimenté par blocs de texte, il reconnaît les éléments au
 * fil de l'eau et interrompt l'analyse au dépassement du nombre de points.
 */
class GpxScanner {
  private buffer = '';
  private readonly stack: string[] = [];
  private rootSeen = false;

  private readonly tracks: OpenElement[] = [];
  private readonly routes: OpenElement[] = [];

  private current: OpenElement | null = null;
  private pending: PendingPoint | null = null;
  private capture: 'name' | 'ele' | null = null;
  private captured = '';
  /** Profondeur d'une extension propre à un fabricant, entièrement ignorée. */
  private ignoreDepth = 0;

  write(chunk: string): void {
    this.buffer += chunk;
    this.consume(false);
  }

  end(): GpxDocument {
    this.consume(true);
    if (this.buffer.trim().length > 0 && this.buffer.includes('<')) {
      throw malformed('une balise est restée inachevée en fin de fichier');
    }
    if (!this.rootSeen) throw malformed('aucun élément <gpx> trouvé');
    if (this.stack.length > 0) {
      throw malformed(`l'élément <${String(this.stack[this.stack.length - 1])}> n'est pas refermé`);
    }

    const elements = this.tracks.length > 0 ? this.tracks : this.routes;
    return {
      elements: elements.map(finalizeElement),
      // Priorité des traces sur les routes : lorsqu'un fichier contient les
      // deux, les routes sont entièrement ignorées.
      ignoredRouteCount: this.tracks.length > 0 ? this.routes.length : 0,
    };
  }

  private consume(final: boolean): void {
    for (;;) {
      const start = this.buffer.indexOf('<');
      if (start < 0) {
        this.appendText(this.buffer);
        this.buffer = '';
        return;
      }
      if (start > 0) {
        this.appendText(this.buffer.slice(0, start));
        this.buffer = this.buffer.slice(start);
      }

      const closing = this.findTagEnd();
      if (closing < 0) {
        // Balise incomplète : on attend le bloc suivant.
        if (final) return;
        return;
      }

      const rawTag = this.buffer.slice(0, closing + 1);
      this.buffer = this.buffer.slice(closing + 1);
      this.handleTag(rawTag);
    }
  }

  /** Fin de la balise en tête de tampon, en tenant compte des sections CDATA. */
  private findTagEnd(): number {
    if (this.buffer.startsWith('<![CDATA[')) {
      const end = this.buffer.indexOf(']]>');
      return end < 0 ? -1 : end + 2;
    }
    if (this.buffer.startsWith('<!--')) {
      const end = this.buffer.indexOf('-->');
      return end < 0 ? -1 : end + 2;
    }
    let inQuote: string | null = null;
    for (let i = 1; i < this.buffer.length; i += 1) {
      const char = this.buffer[i];
      if (char === undefined) break;
      if (inQuote !== null) {
        if (char === inQuote) inQuote = null;
        continue;
      }
      if (char === '"' || char === "'") inQuote = char;
      else if (char === '>') return i;
    }
    return -1;
  }

  private appendText(text: string): void {
    if (this.capture !== null && this.ignoreDepth === 0) this.captured += text;
  }

  private handleTag(rawTag: string): void {
    if (rawTag.startsWith('<![CDATA[')) {
      this.appendText(rawTag.slice(9, -3));
      return;
    }
    if (rawTag.startsWith('<!--') || rawTag.startsWith('<?') || rawTag.startsWith('<!')) return;

    const isClosing = rawTag.startsWith('</');
    const isSelfClosing = rawTag.endsWith('/>');
    const inner = rawTag.slice(isClosing ? 2 : 1, isSelfClosing ? -2 : -1).trim();
    if (inner.length === 0) throw malformed('balise vide');

    const nameMatch = /^[^\s/>]+/.exec(inner);
    if (nameMatch === null) throw malformed('nom de balise absent');
    const qualified = nameMatch[0];
    const name = stripNamespace(qualified);

    if (isClosing) {
      const expected = this.stack.pop();
      if (expected !== qualified) {
        throw malformed(`</${qualified}> ne referme pas <${String(expected)}>`);
      }
      this.closeElement(name);
      return;
    }

    if (!isSelfClosing) this.stack.push(qualified);
    this.openElement(name, inner, isSelfClosing);
    if (isSelfClosing) this.closeElement(name);
  }

  private openElement(name: string, inner: string, selfClosing: boolean): void {
    if (this.ignoreDepth > 0) {
      if (!selfClosing) this.ignoreDepth += 1;
      return;
    }

    switch (name) {
      case 'gpx': {
        if (this.rootSeen) throw malformed('plusieurs éléments <gpx>');
        this.rootSeen = true;
        const version = readAttribute(inner, 'version');
        if (version !== '1.1') {
          throw new DomainError(
            'gpx/not-gpx-11',
            version === null
              ? "Le fichier ne déclare pas de version GPX. Seul le schéma GPX 1.1 est accepté."
              : `Le fichier déclare la version GPX ${version}. Seul le schéma GPX 1.1 est accepté.`,
            version,
          );
        }
        return;
      }
      case 'trk': {
        this.current = { type: 'trk', index: this.tracks.length, name: null, points: new PointBuffer() };
        this.tracks.push(this.current);
        return;
      }
      case 'rte': {
        this.current = { type: 'rte', index: this.routes.length, name: null, points: new PointBuffer() };
        this.routes.push(this.current);
        return;
      }
      case 'trkseg': {
        this.current?.points.openSegment();
        return;
      }
      case 'trkpt':
      case 'rtept': {
        if (this.current === null) return;
        const lat = readNumericAttribute(inner, 'lat');
        const lon = readNumericAttribute(inner, 'lon');
        if (lat === null || lon === null) {
          throw malformed(`<${name}> sans latitude ni longitude exploitables`);
        }
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          throw malformed(`<${name}> porte des coordonnées hors bornes`);
        }
        this.pending = { lat, lon, ele: Number.NaN };
        return;
      }
      case 'name': {
        // Seul le nom direct d'une trace ou d'une route est retenu.
        if (this.current !== null && this.pending === null) {
          this.capture = 'name';
          this.captured = '';
        }
        return;
      }
      case 'ele': {
        if (this.pending !== null) {
          this.capture = 'ele';
          this.captured = '';
        }
        return;
      }
      case 'extensions': {
        // Les extensions propres à un fabricant sont ignorées.
        if (!selfClosing) this.ignoreDepth = 1;
        return;
      }
      default:
        return;
    }
  }

  private closeElement(name: string): void {
    if (this.ignoreDepth > 0) {
      this.ignoreDepth -= 1;
      return;
    }

    switch (name) {
      case 'trk':
      case 'rte': {
        this.current = null;
        return;
      }
      case 'trkpt':
      case 'rtept': {
        const point = this.pending;
        this.pending = null;
        if (point === null || this.current === null) return;
        this.current.points.push(point.lat, point.lon, point.ele);
        if (this.current.points.count > MAX_GPX_POINTS) {
          throw new DomainError(
            'gpx/too-many-points',
            `Le parcours dépasse la limite de ${MAX_GPX_POINTS.toLocaleString('fr-FR')} points : ` +
              `au moins ${this.current.points.count.toLocaleString('fr-FR')} points ont été lus.`,
            String(this.current.points.count),
          );
        }
        return;
      }
      case 'name': {
        if (this.capture === 'name' && this.current !== null && this.current.name === null) {
          const text = decodeEntities(this.captured).trim();
          this.current.name = text.length > 0 ? text : null;
        }
        this.capture = null;
        this.captured = '';
        return;
      }
      case 'ele': {
        if (this.capture === 'ele' && this.pending !== null) {
          const value = Number.parseFloat(decodeEntities(this.captured).trim());
          this.pending.ele = Number.isFinite(value) ? value : Number.NaN;
        }
        this.capture = null;
        this.captured = '';
        return;
      }
      default:
        return;
    }
  }
}

function finalizeElement(element: OpenElement): GpxElement {
  const frozen = element.points.freeze();
  return {
    type: element.type,
    name: element.name,
    index: element.index,
    lat: frozen.lat,
    lon: frozen.lon,
    ele: frozen.ele,
    count: element.points.count,
    segmentStarts: element.points.segmentStarts,
  };
}

function stripNamespace(qualified: string): string {
  const colon = qualified.indexOf(':');
  return colon < 0 ? qualified : qualified.slice(colon + 1);
}

function readAttribute(inner: string, attribute: string): string | null {
  const pattern = new RegExp(`(?:^|\\s)${attribute}\\s*=\\s*("([^"]*)"|'([^']*)')`);
  const match = pattern.exec(inner);
  if (match === null) return null;
  const value = match[2] ?? match[3];
  return value === undefined ? null : decodeEntities(value);
}

function readNumericAttribute(inner: string, attribute: string): number | null {
  const text = readAttribute(inner, attribute);
  if (text === null) return null;
  const value = Number.parseFloat(text);
  return Number.isFinite(value) ? value : null;
}

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      const code = Number.parseInt(entity.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    if (entity.startsWith('#')) {
      const code = Number.parseInt(entity.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[entity] ?? match;
  });
}

/** Analyse un flux de blocs de texte déjà décodés. */
export async function parseGpxChunks(chunks: AsyncIterable<string>): Promise<GpxDocument> {
  const scanner = new GpxScanner();
  for await (const chunk of chunks) scanner.write(chunk);
  return scanner.end();
}

/** Analyse un document GPX tenu en mémoire. Réservé aux tests et aux imports courts. */
export function parseGpxText(text: string): GpxDocument {
  const scanner = new GpxScanner();
  scanner.write(text);
  return scanner.end();
}

/**
 * Analyse un fichier. Le contrôle de taille précède toute lecture du contenu,
 * conformément à EF-GPX-001.
 */
export async function parseGpxFile(file: File): Promise<GpxDocument> {
  if (!file.name.toLowerCase().endsWith('.gpx')) {
    throw new DomainError('gpx/extension', "Le fichier choisi n'a pas l'extension .gpx.", file.name);
  }
  if (file.size > MAX_GPX_BYTES) {
    throw new DomainError(
      'gpx/too-large',
      `Le fichier dépasse la limite de 50 Mo : sa taille est de ${formatMegabytes(file.size)}.`,
      String(file.size),
    );
  }
  return parseGpxChunks(decodeStream(file.stream()));
}

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

async function* decodeStream(stream: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const decoder = new TextDecoder('utf-8');
  const reader = stream.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value !== undefined) yield decoder.decode(value, { stream: true });
    }
    yield decoder.decode();
  } finally {
    reader.releaseLock();
  }
}
